import {
  CarrierSearchIcon,
  CustomerSearchIcon,
  FactoringCompanySearchIcon,
  LoadSearchIcon,
  OfficeSearchIcon,
  PhoneSearchIcon,
  ProspectSearchIcon,
  ReferenceSearchIcon,
  ShipmentMetadataSearchIcon,
  TrackingSearchIcon,
  UserSearchIcon,
  VendorSearchIcon,
} from '@assets';
import { FeatureFlags } from '@constants/featureFlags';
import { resetCarrierDetailsStore } from '@store/carriers';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { resetCustomerStore } from '@store/orders';
import { routeCarrierDetails } from '@utils/routeCarrierDetails';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { isAdmin, isManager } from '@utils/utils';

import {
  CarrierItemsRender,
  CustomerItemsRender,
  FactoringCompanyItemsRender,
  OfficeItemsRender,
  PhoneItemsRender,
  PickupItemsRender,
  ProItemsRender,
  ProspectItemsRender,
  ReferenceItemsRender,
  ShipmentMetaDataItemsRender,
  TrackingItemsRender,
  UserItemsRender,
  VendorItemsRenderer,
} from './components/ItemsRender';
import {
  AutoCompleteRenderType,
  CarrierType,
  CustomerType,
  FactoringCompanyType,
  OfficeType,
  PhoneType,
  PickupType,
  ProspectType,
  ProType,
  ReferenceType,
  SearchItemsType,
  ShipmentMetadataType,
  TrackingType,
  UserType,
  VendorType,
} from './types';

export const searchItems: SearchItemsType[] = [
  {
    label: 'Load',
    disclaimer: 'Search by a Load #',
    icon: LoadSearchIcon,
    show: () => true,
  },
  {
    label: 'Reference #',
    disclaimer: 'Find a Load By Reference#',
    icon: ReferenceSearchIcon,
    show: () => true,
  },
  {
    label: 'Shipment Metadata',
    disclaimer: 'Search custom fields on shipments',
    icon: ShipmentMetadataSearchIcon,
    show: () => true,
  },
  {
    label: 'Carrier',
    disclaimer: 'Search for a carrier',
    icon: CarrierSearchIcon,
    show: () => true,
  },
  {
    label: 'Vendor',
    disclaimer: 'Search by Name or Intacct ID',
    icon: VendorSearchIcon,
    show: () => {
      return isFeatureFlagEnabled(FeatureFlags.VendorManagement)();
    },
  },
  {
    label: 'Customer',
    disclaimer: 'Search for a Customer',
    icon: CustomerSearchIcon,
    show: () => true,
  },
  {
    label: 'Prospect',
    disclaimer: 'Search for a Prospect',
    icon: ProspectSearchIcon,
    show: () => true,
  },
  {
    label: 'Office',
    disclaimer: 'Search for an Office',
    icon: OfficeSearchIcon,
    show: () => isAdmin(),
  },
  {
    label: 'User',
    disclaimer: 'Search for a User',
    icon: UserSearchIcon,
    show: () => isAdmin() || isManager(),
  },
  {
    label: 'Tracking #',
    disclaimer: 'Find a load by Tracking #',
    icon: TrackingSearchIcon,
    show: () => true,
  },
  {
    label: 'Factoring Company',
    disclaimer: 'Search for a Factoring Company',
    icon: FactoringCompanySearchIcon,
    show: () => true,
  },
  {
    label: 'Phone #',
    disclaimer: 'Search by Phone #',
    icon: PhoneSearchIcon,
    show: () => true,
  },
  {
    label: 'Pro #',
    disclaimer: 'Search for an LTL load by pro #',
    icon: ProspectSearchIcon,
    show: () => true,
  },
  {
    label: 'Pickup #',
    disclaimer: 'Search for an LTL load by pickup #',
    icon: ProspectSearchIcon,
    show: () =>
      isFeatureFlagEnabled(FeatureFlags.feature_ltl_search_pickup_number)(),
  },
];

export const autoCompleteMap: Record<string, AutoCompleteRenderType> = {
  'Ref #': {
    version: 'v4',
    endpoint: 'Order/referencenumber?query',
    id: 'autocomplete_reference_search',
    redirect: (item: ReferenceType) => {
      const isNative = routeCarrierDetails(item.orderId);
      if (isNative) {
        return `/Order/Details/${item.orderId}`;
      }
      return '';
    },
    display: (item: ReferenceType) => <ReferenceItemsRender {...item} />,
    minCharacters: 3,
  },
  'Shipment Metadata': {
    version: 'v4',
    endpoint: 'Order/customermetadata?query',
    id: 'autocomplete_shipment_metadata_search',
    redirect: (item: ShipmentMetadataType) => {
      const isNative = routeOrderDetails(item.orderId);
      if (isNative) {
        return `/Order/Details/${item.orderId}`;
      }
      return '';
    },
    display: (item: ShipmentMetadataType) => (
      <ShipmentMetaDataItemsRender {...item} />
    ),
  },
  Carrier: {
    version: 'v4',
    endpoint: 'carrier/autocomplete/?term',
    id: 'autocomplete_carrier_search',
    redirect: (item: CarrierType) => {
      const isNative = routeCarrierDetails(item.id);
      if (isNative) {
        resetCarrierDetailsStore();
        return `/Carrier/Details/${item.id}`;
      }
      return '';
    },
    display: (item: CarrierType) => <CarrierItemsRender {...item} />,
  },
  Customer: {
    version: 'v4',
    endpoint: 'customer/autocomplete/?term',
    id: 'autocomplete_customer_search',
    redirect: (item: CustomerType) => {
      const isNative = routeCarrierDetails(item.id);
      if (isNative) {
        resetCustomerStore();
        return `/Customer/Details/${item.id}`;
      }
      return '';
    },
    display: (item: CustomerType) => <CustomerItemsRender {...item} />,
  },
  Vendor: {
    version: 'v4',
    endpoint: 'Vendor/topbarsearch?query',
    id: 'autocomplete_vendor_search',
    redirect: (item: VendorType) => {
      const isNative = routeCarrierDetails(item.id);

      if (isNative) {
        return `/Vendors/Details/${item.id}`;
      }

      return '';
    },
    display: (vendor: VendorType) => <VendorItemsRenderer vendor={vendor} />,
  },
  Prospect: {
    version: 'v4',
    endpoint: 'ProspectCustomer/autocomplete/?term',
    id: 'autocomplete_prospect_search',
    redirect: (item: ProspectType) => {
      const isNative = routeCarrierDetails(item.id);
      if (isNative) {
        return `/Prospect/Details/${item.id}`;
      }
      return '';
    },
    display: (item: ProspectType) => <ProspectItemsRender {...item} />,
  },
  Office: {
    version: 'v4',
    endpoint: 'office/autocomplete/?term',
    id: 'autocomplete_office_search',
    redirect: (item: OfficeType) => {
      const isNative = routeCarrierDetails(item.id);
      if (isNative) {
        return `/Office/Details/${item.id}`;
      }
      return '';
    },
    display: (item: OfficeType) => <OfficeItemsRender {...item} />,
  },
  User: {
    version: 'v4',
    endpoint: 'user/autocomplete?includeInactiveUsers=true&term',
    id: 'autocomplete_user_search',
    redirect: (item: UserType) => {
      const isNative = routeCarrierDetails(item.id);
      if (isNative) {
        return `/User/Details/${item.id}`;
      }
      return '';
    },
    display: (item: UserType) => <UserItemsRender {...item} />,
  },
  'Tracking #': {
    version: 'v4',
    endpoint: 'load/trackingnumber?query',
    id: 'autocomplete_tracking_search',
    redirect: (item: TrackingType) => {
      const isNative = routeOrderDetails(item.orderId);
      if (isNative) {
        return `/Order/Details/${item.orderId}`;
      }
      return '';
    },
    display: (item: TrackingType) => <TrackingItemsRender {...item} />,
  },
  'Factoring Company': {
    version: 'v4',
    endpoint: 'FactoringCompany/factoringautocomplete/?term',
    id: 'autocomplete_factoringcompany_search',
    redirect: (item: FactoringCompanyType) => {
      const isNative = routeCarrierDetails(item.id);
      if (isNative) {
        return `/FactoringCompany/Details/${item.id}`;
      }
      return '';
    },
    display: (item: FactoringCompanyType) => (
      <FactoringCompanyItemsRender {...item} />
    ),
  },
  'Phone #': {
    version: 'v4',
    endpoint: 'GlobalSearch/phoneNumber?query',
    id: 'autocomplete_phone_search',
    redirect: (item: PhoneType) => {
      const isNative = routeCarrierDetails(item.objectId);
      if (isNative) {
        return `/${item.contactType}/Details/${item.objectId}`;
      }
      return '';
    },
    display: (item: PhoneType) => <PhoneItemsRender {...item} />,
    minCharacters: 9,
  },
  'Pro #': {
    version: 'v4',
    endpoint: '/Order/ProNumber?query',
    id: 'autocomplete_pro_search',
    redirect: (item: ProType) => {
      const isNative = routeOrderDetails(item.orderId);
      if (isNative) {
        return `/Order/Details/${item.orderId}`;
      }
      return '';
    },
    display: (item: ProType) => <ProItemsRender {...item} />,
    minCharacters: 3,
  },
  'Pickup #': {
    version: 'v4',
    endpoint: 'Order/referencenumbertype?type=Pickup&query',
    id: 'autocomplete_pickup_search',
    redirect: (item: PickupType) => {
      const isNative = routeOrderDetails(item.orderId);
      if (isNative) {
        return `/Order/Details/${item.orderId}`;
      }
      return '';
    },
    display: (item: PickupType) => <PickupItemsRender {...item} />,
  },
};
